import React, { memo, useCallback, useState } from 'react';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { SearchTemplate } from '@app/templates';
import { Tender, UserType } from '@app/models';
import { getTenders, PageableParams } from '@app/api';
import { Info } from '@app/components';
import { useUserContext } from '@app/providers';
import { dateFormat } from '@app/helpers';

interface Props {
  onSubmit: (tenderId: string | null) => void;
}

function Step3(props: Props) {
  const { onSubmit } = props;
  const { user, company } = useUserContext();
  const [tenderId, setTenderId] = useState<string>('');

  const getData = useCallback(
    (params: PageableParams) => {
      const defaultParams: Partial<Tender> = {};
      if (user.type === UserType.INDIVIDUAL) {
        defaultParams.ownerId = user.id;
      } else {
        defaultParams.companyId = company!.id;
      }
      return getTenders({
        ...params,
        ...defaultParams,
      });
    },
    [user, company]
  );

  const onClickSubmit = useCallback(() => {
    onSubmit(!!tenderId ? tenderId : null);
  }, [tenderId, onSubmit]);

  const renderItem = useCallback(
    (item: Tender) => {
      const isSelected = tenderId === item.id;

      return (
        <div
          className={`
            p-3 sm:p-4 bg-muted rounded-lg cursor-pointer transition-all duration-200
            hover:bg-muted/80 
            ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}
          `}
          onClick={() => setTenderId(item.id)}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 items-start lg:items-center">
            <div className="lg:col-span-1">
              <Info label="Наименование" value={item.name} small={true} />
            </div>
            <div className="lg:col-span-1">
              <Info label="Цена" value={item.price} small={true} />
            </div>
            <div className="lg:col-span-1">
              <Info
                label="Дата подачи"
                value={dateFormat(item.createdAt)}
                small={true}
              />
            </div>
            <div className="flex justify-start lg:justify-center">
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => setTenderId(item.id)}
              />
            </div>
          </div>
        </div>
      );
    },
    [tenderId]
  );

  return (
    <div className="flex flex-col w-full max-w-full h-full">
      <div className="flex-1">
        <SearchTemplate<Tender>
          variant="paper"
          renderItem={renderItem}
          title="Выберите объявление (-я)"
          getData={getData}
          emptyTitle="Нет объявлений"
          horizontal={true}
          viewControl={false}
        />
      </div>
      <div className="flex justify-end gap-3 sm:gap-4 p-3 sm:p-4 border-t bg-background/50 flex-shrink-0 mt-4">
        <Button
          onClick={onClickSubmit}
          className="w-full sm:w-auto min-w-[120px]"
        >
          Продолжить
        </Button>
      </div>
    </div>
  );
}

export default memo(Step3);
